// Tipo de exposição na consignação
export type ConsignmentExposure = 'store' | 'owner';

// Tipo de comissão na consignação: % sobre a venda ou valor fixo (R$)
export type ConsignmentCommissionType = 'percent' | 'fixed';

export const CONSIGNMENT_COMMISSION_TYPE_LABELS: { [key in ConsignmentCommissionType]: string } = {
  percent: 'Porcentagem (%)',
  fixed: 'Valor fixo (R$)',
};

// Tipos para veículos
export interface Vehicle {
  documents?: any[];
  sale_price: number;
  sale_price_promo: number;
  codigo_ano: any;
  vehicleType: string;
  codigo_fipe: string;
  id: number;
  brand: string;
  brand_id?: number;
  model: string;
  model_id?: number;
  year: number;
  plate?: string;
  /** Vitrine pública: só o último caractere da placa (a placa inteira não é exposta). */
  plate_end?: string | null;
  /** Veículo 0 km ou sem emplacamento: permite plate vazio. */
  no_plate?: boolean;
  /** Para 'outro veículo' (jet/quadriciclo): identificação no lugar de placa. */
  identification?: string;
  /** Observações internas (somente equipe). */
  internal_notes?: string;
  color: string;
  purchase_price: number;
  purchase_date: string;
  status: 'available' | 'sold' | 'maintenance';
  chassis?: string;
  notes?: string;
  expenses?: Expense[];
  images?: {id: number, url: string}[]; // URLs das imagens do veículo
  km?: string;
  /** Para 'outro veículo' (jet/quadriciclo): horas de uso no lugar de km. */
  hours?: string;
  cambio: string;
  motor: string;
  // Consignação: veículo deixado na loja para venda, sem transferência imediata de propriedade
  is_consignado?: boolean;
  /** Tipo da comissão da loja na consignação */
  consignment_commission_type?: ConsignmentCommissionType;
  /** Valor da comissão: percentual (0-100) se type=percent, ou valor em R$ se type=fixed */
  consignment_commission?: number;
  consignment_exposure?: ConsignmentExposure; // 'store' = na loja | 'owner' = com o dono
  consignment_owner_name?: string;
  consignment_owner_contact?: string;
  consignment_contract_date?: string;
  consignment_notes?: string;

  // ===== Vitrine pública (controle por cadastro) =====
  /** Mostrar na seção do topo da vitrine. */
  showcase_top?: boolean;
  /** Mostrar como “carro em destaque”. */
  showcase_featured?: boolean;
  /** Mostrar como “destaque da semana”. */
  showcase_week_highlight?: boolean;
  /**
   * Prioridade (ordem) para destaque no topo/destaques.
   * Maior = aparece primeiro. Se ausente, usa fallback por data/id.
   */
  showcase_priority?: number | null;
}

// ========= Leads gerais (CRM) =========
export type LeadStatus =
  | 'novo'
  | 'em_atendimento'
  | 'negociacao'
  | 'proposta'
  | 'ganho'
  | 'perdido'
  | 'pos_venda';

export const LEAD_STATUS_LABELS: { [key in LeadStatus]: string } = {
  novo: 'Novo',
  em_atendimento: 'Em atendimento',
  negociacao: 'Negociação',
  proposta: 'Proposta',
  ganho: 'Ganho',
  perdido: 'Perdido',
  pos_venda: 'Pós-venda',
};

export const LEAD_STATUS_COLORS: {
  [key in LeadStatus]: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
} = {
  novo: 'info',
  em_atendimento: 'primary',
  negociacao: 'warning',
  proposta: 'primary',
  ganho: 'success',
  perdido: 'error',
  pos_venda: 'default',
};

export type LeadChannel =
  | 'instagram'
  | 'whatsapp'
  | 'indicacao'
  | 'olx'
  | 'webmotors'
  | 'visita'
  | 'facebook'
  | 'google'
  | 'outro';

export const LEAD_CHANNEL_LABELS: { [key in LeadChannel]: string } = {
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  indicacao: 'Indicação',
  olx: 'OLX',
  webmotors: 'Webmotors',
  visita: 'Visita',
  facebook: 'Facebook',
  google: 'Google',
  outro: 'Outro',
};

export type LeadPaymentMethod =
  | 'avista'
  | 'financiamento'
  | 'consorcio'
  | 'troca_mais_financiamento'
  | 'outro';

export const LEAD_PAYMENT_METHOD_LABELS: { [key in LeadPaymentMethod]: string } = {
  avista: 'À vista',
  financiamento: 'Financiamento',
  consorcio: 'Consórcio',
  troca_mais_financiamento: 'Troca + financiamento',
  outro: 'Outro',
};

export type LeadPriceRange = 'ate_50k' | '50_80k' | '80_120k' | '120k_mais';

export const LEAD_PRICE_RANGE_LABELS: { [key in LeadPriceRange]: string } = {
  ate_50k: 'Até R$ 50 mil',
  '50_80k': 'R$ 50–80 mil',
  '80_120k': 'R$ 80–120 mil',
  '120k_mais': 'R$ 120 mil+',
};

export type LeadAlert = 'nenhum' | 'atencao' | 'urgente';

export const LEAD_ALERT_LABELS: { [key in LeadAlert]: string } = {
  nenhum: 'Nenhum',
  atencao: 'Atenção',
  urgente: 'Urgente',
};

export const LEAD_ALERT_COLORS: {
  [key in LeadAlert]: 'default' | 'warning' | 'error';
} = {
  nenhum: 'default',
  atencao: 'warning',
  urgente: 'error',
};

export interface Lead {
  id: number;
  name: string;
  whatsapp: string;
  email?: string | null;
  city_neighborhood?: string | null;
  first_contact_at?: string | null;
  origin?: LeadChannel | null;
  secondary_origin?: LeadChannel | null;
  current_channel?: LeadChannel | null;
  vehicle_interest?: string | null;
  price_range?: LeadPriceRange | null;
  has_trade_in?: boolean;
  trade_in_vehicle?: string | null;
  payment_method?: LeadPaymentMethod | null;
  status: LeadStatus;
  seller_id?: number | null;
  seller?: Employee | null;
  last_contact_at?: string | null;
  next_contact_at?: string | null;
  next_contact_action?: string | null;
  alert?: LeadAlert | null;
  purchased_vehicle?: string | null;
  purchase_date?: string | null;
  trade_forecast_or_birthday?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type LeadPayload = Omit<Lead, 'id' | 'seller' | 'created_at' | 'updated_at'>;

export type MaintenancePriority = 'low' | 'medium' | 'high';
export type MaintenanceItemStatus = 'pending' | 'in_progress' | 'done' | 'cancelled';

export interface MaintenanceItem {
  id: number;
  vehicle_id: number;
  description: string;
  estimated_amount?: number | null;
  priority: MaintenancePriority;
  status: MaintenanceItemStatus;
  internal_notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const CONSIGNMENT_EXPOSURE_LABELS: { [key in ConsignmentExposure]: string } = {
  store: 'Na loja (para test-drive)',
  owner: 'Com o dono (anúncio online)',
};

// Tipos para despesas
export interface Expense {
  comission_value: any;
  commission_type: string;
  seller: any;
  status: string;
  id: number;
  vehicle_id: number;
  vehicle?: Vehicle;
  category: ExpenseCategory;
  description: string;
  amount: number;
  expense_date: string;
  url_documento?: string;
  notes?: string;
  /**
   * Se true (ou omitido), a despesa reduz o caixa da loja no resumo.
   * Se false, conta só no lucro do veículo (não reduz o total de despesas do caixa).
   */
  deduct_from_store_cash?: boolean;
}

/** Despesa entra no cálculo de saída de caixa da loja (padrão: sim). */
export const expenseAffectsStoreCash = (e: Pick<Expense, 'deduct_from_store_cash'>): boolean => {
  const v: unknown = (e as { deduct_from_store_cash?: unknown }).deduct_from_store_cash;
  if (v === false || v === 0 || v === '0') return false;
  return true;
};

export type ExpenseCategory = 
  | 'maintenance' // Manutenção geral
  | 'mechanics' // Mecânica
  | 'bodywork' // Funilaria
  | 'upholstery' // Tapeçaria
  | 'fuel' // Combustível
  | 'insurance' // Seguro
  | 'tax' // Impostos e taxas
  | 'accessories' // Acessórios
  | 'cleaning' // Limpeza
  | 'other'; // Outros

export interface ExpenseCategorySummary {
  category: ExpenseCategory;
  amount: number;
  count: number;
}

// Constantes para categorias de despesas
export const EXPENSE_CATEGORIES: { [key in ExpenseCategory]: string } = {
  maintenance: 'Manutenção Geral',
  mechanics: 'Mecânica',
  bodywork: 'Funilaria',
  upholstery: 'Tapeçaria',
  fuel: 'Combustível',
  insurance: 'Seguro',
  tax: 'Impostos e Taxas',
  accessories: 'Acessórios',
  cleaning: 'Limpeza',
  other: 'Outros',
};

// Cores para categorias de despesas
export const EXPENSE_CATEGORY_COLORS: { [key in ExpenseCategory]: string } = {
  maintenance: '#3498db', // Azul
  mechanics: '#e74c3c', // Vermelho
  bodywork: '#f39c12', // Laranja
  upholstery: '#9b59b6', // Roxo
  fuel: '#2ecc71', // Verde
  insurance: '#1abc9c', // Verde água
  tax: '#34495e', // Azul escuro
  accessories: '#16a085', // Turquesa
  cleaning: '#3498db', // Azul
  other: '#7f8c8d', // Cinza
};

// Tipos para vendas
export type PaymentMethod = 
  | 'money' // Dinheiro
  | 'pix' // PIX
  | 'check' // Cheque
  | 'financing' // Financiamento
  | 'term' // A prazo (entrada + parcelas direto com a loja)
  | 'bank_transfer' // Transferência Bancária
  | 'other'; // Outro

export type PaymentStatus = 'pending' | 'paid' | 'partial';

/** Documento anexado à venda (comprovante, contrato, etc.) */
export interface SaleDocument {
  id: number;
  url: string;
  type?: string;
  file_name?: string;
}

/**
 * Parcela da venda “a prazo” — cobrança individual com baixa na API.
 * Gerada pelo backend ao salvar a venda (a partir dos valores/datas combinados).
 */
export interface SaleInstallment {
  id: number;
  sale_id: number;
  /** Ordem após a entrada (1 = primeira parcela) */
  sequence: number;
  /** Vencimento (YYYY-MM-DD ou ISO). */
  due_date: string;
  amount: number;
  status: PaymentStatus;
  paid_at?: string | null;
}

/** Tipo de comissão do vendedor na venda: valor fixo em R$, % sobre valor da venda ou % sobre margem de lucro */
export type SaleCommissionType = 'fixed' | 'percent_sale' | 'percent_profit';

export const SALE_COMMISSION_TYPE_LABELS: { [key in SaleCommissionType]: string } = {
  fixed: 'Valor fixo (R$)',
  percent_sale: '% sobre valor da venda',
  percent_profit: '% sobre margem de lucro',
};

export interface Sale {
  bank_commission_percent: number;
  bank: string;
  financed_amount: any;
  down_payment: any;
  documents?: SaleDocument[];
  id: number;
  vehicle_id: number;
  vehicle?: Vehicle;
  sale_price: number;
  sale_date: string;
  customer_name: string;
  customer_contact: string;
  payment_method: PaymentMethod;
  buyer_name: string;
  buyer_phone: string;
  buyer_document: string;
  /** E-mail do cliente */
  buyer_email?: string;
  /** Endereço completo do cliente */
  buyer_address?: string;
  buyer_city?: string;
  buyer_state?: string;
  buyer_zip?: string;
  payment_status: PaymentStatus;
  notes?: string;
  /** ID do funcionário responsável pela venda (vendedor) */
  seller_id?: number;
  /** Dados do vendedor (quando retornado pela API) */
  seller?: Employee;
  /** Tipo da comissão do vendedor nesta venda */
  commission_type?: SaleCommissionType;
  /** Valor: em R$ (fixed) ou percentual 0-100 (percent_sale / percent_profit) */
  commission_value?: number;
  /** Valor calculado da comissão em R$ (pode vir do backend ou ser calculado no front) */
  commission_amount?: number;
  /** Data prevista do vencimento da próxima prestação (pagamento a prazo) */
  next_installment_due_date?: string | null;
  /** Quantidade de parcelas após a entrada (pagamento a prazo) */
  installment_count?: number | null;
  /**
   * Valores de cada parcela após a entrada (índice 0 = 1ª parcela), em R$.
   * Pode vir como array ou como JSON string da API.
   */
  installment_amounts?: number[] | string | null;
  /** Valor único legado quando todas as parcelas eram iguais (preferir installment_amounts) */
  installment_amount?: number | null;
  /** Parcelas com controle de pagamento (quando a API já gerou/registrou). */
  installments?: SaleInstallment[];
}

export const PAYMENT_METHODS: { [key in PaymentMethod]: string } = {
  money: 'Dinheiro',
  pix: 'PIX',
  check: 'Cheque',
  financing: 'Financiamento',
  term: 'A prazo',
  bank_transfer: 'Transferência Bancária',
  other: 'Outro',
};

export const BANKS = {
  itau: { name: 'Itaú', commission: 3 },
  bradesco: { name: 'Bradesco', commission: 2.5 },
  santander: { name: 'Santander', commission: 2.8 },
  caixa: { name: 'Caixa', commission: 2.2 }
};
export const PAYMENT_STATUS: { [key in PaymentStatus]: string } = {
  pending: 'Pendente',
  partial: 'Parcial',
  paid: 'Pago',
};

// ========= Locação de veículos =========
/** Cobrança por diárias ou mensalidade (contrato recorrente fixo). */
export type VehicleRentalBillingType = 'daily' | 'monthly';

export const VEHICLE_RENTAL_BILLING_LABELS: { [key in VehicleRentalBillingType]: string } = {
  daily: 'Diárias',
  monthly: 'Mensalidade',
};

export type VehicleRentalStatus = 'pending' | 'active' | 'completed' | 'cancelled';

export const VEHICLE_RENTAL_STATUS_LABELS: { [key in VehicleRentalStatus]: string } = {
  pending: 'Pendente',
  active: 'Ativa',
  completed: 'Encerrada',
  cancelled: 'Cancelada',
};

/** Parcela / cobrança agendada da locação (gerada pelo backend). */
export interface VehicleRentalInstallment {
  id: number;
  vehicle_rental_id: number;
  due_date: string;
  amount: number;
  status: PaymentStatus;
  paid_at?: string | null;
}

export interface VehicleRental {
  id: number;
  vehicle_id: number;
  vehicle?: Vehicle;
  billing_type: VehicleRentalBillingType;
  status: VehicleRentalStatus;
  /** Início (YYYY-MM-DD). */
  start_date: string;
  /** Fim previsto (diárias) ou fim do contrato mensal; opcional se contrato aberto. */
  end_date?: string | null;
  /** Valor unitário da diária (billing_type = daily). */
  daily_rate?: number | null;
  /** Valor fixo por mês (billing_type = monthly). */
  monthly_amount?: number | null;
  /** Quantidade de diárias (pode ser calculada no backend). */
  number_of_days?: number | null;
  /** Valor total fechado do período (opcional). */
  total_amount?: number | null;
  /** Locatário — mesmo padrão conceitual do comprador em vendas. */
  renter_name: string;
  renter_phone: string;
  renter_document: string;
  renter_email?: string;
  /** Endereço do locatário (opcional). */
  renter_zip?: string;
  renter_address?: string;
  renter_number?: string;
  renter_complement?: string;
  renter_neighborhood?: string;
  renter_city?: string;
  renter_state?: string;
  payment_method: PaymentMethod;
  payment_status: PaymentStatus;
  notes?: string;
  /** Próximo vencimento (resumo). */
  next_due_date?: string | null;
  installments?: VehicleRentalInstallment[];
  created_at?: string;
  updated_at?: string;
}

// ========= Funcionários =========
/** Cargo do funcionário (vendedor tem salário + comissão + meta) */
export type EmployeeRole = 'vendedor' | 'gerente' | 'consultor' | 'outro';

export interface Employee {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  role: EmployeeRole;
  /** Salário fixo mensal (R$) */
  base_salary: any;
  /** Percentual de comissão sobre vendas (ex: 2.5 = 2,5%) - aplicável a vendedores */
  commission_percent?: number;
  /** Bônus por meta cumprida (R$) - ex: valor fixo ao bater meta mensal */
  target_bonus?: number;
  /** Meta de vendas mensal (quantidade ou valor) - para vendedores */
  monthly_sales_target?: number;
  /** Se a meta é em quantidade de carros (true) ou valor em R$ (false) */
  target_in_units?: boolean;
  admission_date?: string;
  notes?: string;
  active?: boolean;
}

export const EMPLOYEE_ROLES: { [key in EmployeeRole]: string } = {
  vendedor: 'Vendedor',
  gerente: 'Gerente',
  consultor: 'Consultor',
  outro: 'Outro',
};

// ========= Interfaces para Gráficos =========

// Interface para dados de gráfico de barras
export interface BarChartData {
  labels: string[];
  values: number[];
  label?: string;
}

// Interface para dados de gráfico de pizza
export interface PieChartData {
  labels: string[];
  values: number[];
}

// Interface para dados de gráfico de linha
export interface LineChartData {
  labels: string[];
  values: number[];
  label?: string;
}

// Interface para dados de gráfico com dois eixos (barra e linha)
export interface DualAxisChartData {
  labels: string[];
  barData: {
    values: number[];
    label: string;
  };
  lineData: {
    values: number[];
    label: string;
  };
} 


export interface Banco {
    id: number;
    nome: string;
    host: string;
    database: string;
    username: string;
    data_expiracao: string;
    password?:string;

  }
export interface BancoModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Banco) => void;
  banco?: Banco | null;
}

export interface MigrationLog {
  banco: string;
  comando: string;
  saida: string;
  created_at: string;
}

export interface MigrationModalProps {
  open: boolean;
  onClose: () => void;
  banco: string | null;
}

// ========= Caixa (fluxo de caixa) =========
export type TipoMovimentacaoCaixa = 'entrada' | 'saida';

/** Categorias para entradas de caixa (movimentações tipo entrada) */
export type CategoriaEntradaCaixa =
  | 'vendas_acessorios'
  | 'servicos'
  | 'comissao_consignacao'
  | 'reembolso'
  | 'outros_recebimentos';

/** Categorias para saídas de caixa (movimentações tipo saída) */
export type CategoriaSaidaCaixa =
  | 'despesas_gerais'
  | 'aluguel'
  | 'energia_agua'
  | 'telefonia_internet'
  | 'propaganda'
  | 'impostos_taxas'
  | 'outros_pagamentos';

export const CATEGORIAS_ENTRADA_CAIXA: { value: CategoriaEntradaCaixa; label: string }[] = [
  { value: 'vendas_acessorios', label: 'Vendas de acessórios' },
  { value: 'servicos', label: 'Serviços prestados' },
  { value: 'comissao_consignacao', label: 'Comissão de consignação' },
  { value: 'reembolso', label: 'Reembolso' },
  { value: 'outros_recebimentos', label: 'Outros recebimentos' },
];

export const CATEGORIAS_SAIDA_CAIXA: { value: CategoriaSaidaCaixa; label: string }[] = [
  { value: 'despesas_gerais', label: 'Despesas gerais' },
  { value: 'aluguel', label: 'Aluguel' },
  { value: 'energia_agua', label: 'Energia / Água' },
  { value: 'telefonia_internet', label: 'Telefonia / Internet' },
  { value: 'propaganda', label: 'Propaganda / Marketing' },
  { value: 'impostos_taxas', label: 'Impostos e taxas' },
  { value: 'outros_pagamentos', label: 'Outros pagamentos' },
];

export const CATEGORIA_ENTRADA_LABEL: Record<CategoriaEntradaCaixa, string> = {
  vendas_acessorios: 'Vendas de acessórios',
  servicos: 'Serviços prestados',
  comissao_consignacao: 'Comissão de consignação',
  reembolso: 'Reembolso',
  outros_recebimentos: 'Outros recebimentos',
};

export const CATEGORIA_SAIDA_LABEL: Record<CategoriaSaidaCaixa, string> = {
  despesas_gerais: 'Despesas gerais',
  aluguel: 'Aluguel',
  energia_agua: 'Energia / Água',
  telefonia_internet: 'Telefonia / Internet',
  propaganda: 'Propaganda / Marketing',
  impostos_taxas: 'Impostos e taxas',
  outros_pagamentos: 'Outros pagamentos',
};

/** Conta a pagar: pendente até dar baixa no caixa; quitada debita o caixa na data do pagamento. */
export type PayableStatusMovimentacao = 'pending' | 'paid';

export interface MovimentacaoCaixa {
  id: string;
  type: TipoMovimentacaoCaixa;
  category?: CategoriaEntradaCaixa | CategoriaSaidaCaixa; // conforme type
  amount: number;
  description: string;
  date: string; // ISO date (competência ou data do pagamento, conforme o caso)
  createdAt?: string;
  /** Vencimento (obrigatório quando conta a pagar pendente). */
  dueDate?: string;
  /** Se `pending`, a saída ainda não debita o caixa até dar baixa. */
  payableStatus?: PayableStatusMovimentacao;
  /** Texto exibido nos avisos de vencimento (ex.: lembrete ao pagar). */
  payableNote?: string;
}

/**
 * Movimentação que já entra no saldo do caixa.
 * Saídas marcadas como conta a pagar `pending` não debitam até a baixa.
 */
export const movimentacaoAfetaSaldoCaixa = (m: MovimentacaoCaixa): boolean => {
  if (m.type === 'entrada') return true;
  const st: unknown = m.payableStatus;
  return st !== 'pending';
};

/** Resumo do caixa: Total entradas = receita real (vendas) + entradas mov.; Lucro = Total entradas - Saídas */
export interface CaixaResumo {
  /** Faturamento bruto (soma dos sale_price) — referência */
  totalVendas: number;
  /** Lucro real agregado das vendas (direto: venda − compra − despesas; consignado: comissão − despesas) */
  receitaRealVendas: number;
  totalMovimentacoesEntrada: number;
  totalMovimentacoesSaida: number;
  totalDespesas: number;
  totalEntradas: number;
  totalSaidas: number;
  lucroCaixa: number;
  movimentacoes: MovimentacaoCaixa[];
}
